import React, { useState } from "react";

import { Close as CloseIcon } from "@mui/icons-material";
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";

import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";

import {
    deleteSource,
    deleteSourceFromTracker,
} from "helpers/Sources/deleteSource";

import SwalAlertComp from "components/SwalAlert";

import { getSingleTrackerRedux } from "../../../../../redux/slices/trackers/trackers.asyncActions";
import {
    Language,
    Source,
    SourceModalMode,
    Tracker,
} from "../SourceModal.types";
import SourceIcon from "./SourceIcon";

interface DeleteSourceDialogProps {
    open: boolean;
    onClose: () => void;
    source: Source;
    tracker?: Tracker;
    language: Language;
    onDeleteSuccess?: () => void;
    mode?: SourceModalMode;
    onRefresh?: () => void;
}

const DeleteSourceDialog: React.FC<DeleteSourceDialogProps> = ({
    open,
    onClose,
    source,
    language,
    onDeleteSuccess,
    mode = "tracker",
    onRefresh,
}) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const params = useParams<{ id: string }>();

    const isStandalone = mode === "standalone";

    const [isPermanentDelete, setIsPermanentDelete] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleClose = () => {
        if (!isDeleting) {
            setIsPermanentDelete(false);
            onClose();
        }
    };

    const handleDelete = async () => {
        if (!source?.id) return;

        if (!isStandalone && !params.id) return;

        setIsDeleting(true);
        try {
            let response: any;

            if (isStandalone) {
                response = await (deleteSource as any)(source.id);
            } else {
                response = await (deleteSourceFromTracker as any)(
                    params.id,
                    source.id,
                    isPermanentDelete,
                );
            }

            if (response.status) {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.success ?? "Success",
                    message: response.message,
                    icon: "success",
                    timer: 3000,
                });

                if (onRefresh) {
                    onRefresh();
                } else if (params.id) {
                    // @ts-ignore - JS thunk call
                    dispatch(getSingleTrackerRedux(params.id));
                }

                handleClose();
                onDeleteSuccess?.();
            } else {
                SwalAlertComp({
                    title: language?.delete_swal_modal?.failed ?? "Failed",
                    message: response.message,
                    icon: "error",
                });
            }
        } catch (error) {
            console.log("error", error);
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: isStandalone
                    ? "An error occurred while deleting the source"
                    : "An error occurred while removing the source",
                icon: "error",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pb: 1,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Typography variant="h6" component="div">
                        {isStandalone
                            ? "Delete Source"
                            : (language?.buttons?.remove_source ??
                              "Remove Source")}
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    disabled={isDeleting}
                    size="small"
                    sx={{
                        color: theme.palette.text.secondary,
                        "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 3 }}>
                <Box sx={{ mb: 3, mt: 3 }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                    >
                        {isStandalone
                            ? "This action will permanently delete the source. This cannot be undone."
                            : "This action will remove the source from your tracker. This cannot be undone."}
                    </Typography>
                </Box>

                <Stack spacing={3}>
                    <Box>
                        <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                        >
                            Source Information
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                p: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1,
                            }}
                        >
                            <SourceIcon
                                code={source?.source_type?.code}
                                size="medium"
                            />
                            <Box>
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500 }}
                                >
                                    {source?.source_type?.name}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {source?.name}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {!isStandalone && source?.deletable && (
                        <Box>
                            <Typography
                                variant="subtitle2"
                                gutterBottom
                                sx={{ fontWeight: 600 }}
                            >
                                Deletion Options
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isPermanentDelete}
                                        onChange={(e) =>
                                            setIsPermanentDelete(
                                                e.target.checked,
                                            )
                                        }
                                        disabled={isDeleting}
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            sx={{ fontWeight: 500 }}
                                        >
                                            {language?.source_delete_dialog
                                                ?.checkbox_message ??
                                                "Remove completely from system"}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            This will permanently delete the
                                            source and all associated data
                                        </Typography>
                                    </Box>
                                }
                            />
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{ p: 3, gap: 1, justifyContent: "space-between" }}
            >
                <Button
                    onClick={handleClose}
                    disabled={isDeleting}
                    variant="outlined"
                >
                    Cancel
                </Button>

                <div className="position-relative overflow-hidden z-1">
                    {isDeleting && (
                        <div className="circle-loader max-height-45">
                            <ClipLoader color={"#000"} size={17} />
                        </div>
                    )}
                    <Button
                        onClick={handleDelete}
                        variant="contained"
                        color="error"
                    >
                        {isDeleting
                            ? isStandalone
                                ? "Deleting..."
                                : "Removing..."
                            : isStandalone
                              ? "Delete"
                              : (language?.buttons?.remove ?? "Remove")}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteSourceDialog;
